import React, { useEffect, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

/**
 * CustomCursor — realistic revolving Earth.
 *
 * Behaviour:
 *  - Over PLAIN content (body, headings, images, backgrounds): custom
 *    Earth globe cursor is shown, browser cursor is hidden.
 *  - Over INTERACTIVE elements (buttons, links, cards, pills, thumbs) or
 *    over FORM FIELDS (input/textarea/select/contenteditable): the globe
 *    is hidden completely and the browser's own arrow / hand / I-beam
 *    cursor takes over. This is what users expect on the elements they
 *    actually click.
 *  - After 5s of no mouse activity, the globe fades out entirely.
 *
 * The globe itself is a sphere-clipped strip containing TWO real-world
 * maps side-by-side (via react-simple-maps + world-atlas topojson),
 * translated infinitely to give the illusion of Y-axis rotation.
 */

const IDLE_HIDE_MS = 5000;
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";
const GLOBE_PROJECTION_CONFIG = { scale: 32 };
const GLOBE_MAP_STYLE = { width: "100%", height: "100%", display: "block" };

export default function CustomCursor() {
  const wrapRef = useRef(null);
  const rafRef = useRef(0);
  const target = useRef({ x: -100, y: -100 });
  const pos = useRef({ x: -100, y: -100 });
  const idleTimer = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [show, setShow] = useState(false);         // physically rendered?
  const [nativeCursor, setNativeCursor] = useState(false); // over an interactive element?

  useEffect(() => {
    const canHover =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    setEnabled(canHover);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.add("cursor-custom-active");

    const scheduleIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setShow(false), IDLE_HIDE_MS);
    };

    const isInteractive = (el) => {
      if (!el || !el.closest) return false;
      return !!el.closest(
        "a, button, .btn, [role='button'], input, textarea, select, [contenteditable='true'], .pill, .thumb, summary, label, iframe, [data-cursor='native'], .ptile, .stile, .industry-tile, .card"
      );
    };

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      const interactive = isInteractive(e.target);
      setNativeCursor(interactive);
      // Globe visible only over non-interactive content
      setShow(!interactive);
      scheduleIdle();
    };

    const onLeave = () => setShow(false);

    const tick = () => {
      const dx = target.current.x - pos.current.x;
      const dy = target.current.y - pos.current.y;
      pos.current.x += dx * 0.22;
      pos.current.y += dy * 0.22;
      const el = wrapRef.current;
      if (el) {
        el.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0) translate(-50%, -50%)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    scheduleIdle();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.documentElement.classList.remove("cursor-custom-active");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onMove);
      document.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(rafRef.current);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [enabled]);

  // Toggle a body class so CSS can decide whether to hide the browser cursor
  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.toggle("cursor-native", nativeCursor);
  }, [enabled, nativeCursor]);

  if (!enabled) return null;

  return (
    <div
      ref={wrapRef}
      className={`cursor-globe ${show ? "is-visible" : ""}`}
      aria-hidden="true"
    >
      <div className="cursor-globe__shadow" />
      <div className="cursor-globe__ball">
        <div className="cursor-globe__strip">
          <GlobeMap />
          <GlobeMap />
        </div>
        <div className="cursor-globe__sheen" />
      </div>
    </div>
  );
}

/**
 * A tiny equirectangular world map rendered from real topojson.
 * Rendered twice inside the strip for seamless infinite scrolling.
 * Land = warm green, ocean = transparent (shows the navy ball).
 */
function GlobeMap() {
  return (
    <div className="cursor-globe__map">
      <ComposableMap
        projection="geoEquirectangular"
        projectionConfig={GLOBE_PROJECTION_CONFIG}
        width={200}
        height={100}
        style={GLOBE_MAP_STYLE}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((g) => (
              <Geography
                key={g.rsmKey}
                geography={g}
                style={{
                  default: {
                    fill: "#79c47c",
                    stroke: "#2d6a3a",
                    strokeWidth: 0.25,
                    outline: "none",
                  },
                  hover: { fill: "#79c47c", outline: "none" },
                  pressed: { fill: "#79c47c", outline: "none" },
                }}
              />
            ))
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
}
